import { Trash2 } from "lucide-react";
import { FileMark } from "@/components/ui/FileMark";
import { Menu, MenuItem } from "@/components/ui/Menu";
import { IconButton } from "@/components/ui/button";
import { MicroLabel } from "@/components/ui/MicroLabel";
import { Panel } from "@/components/ui/Panel";
import { OpenDocument } from "../documents/OpenDocument";
import type { FileResponse, LiteratureType } from "@patent-studio/api-types";
import { Checkbox } from "@/components/ui/checkbox";
import { type PillTone, StatusPill } from "@/components/ui/StatusPill";
import { cn } from "@/lib/utils";
import {
  ReadStatusChip,
  needsAttention,
  readOf,
} from "@/components/patterns/ReadStatusChip";
import { priorArtTypeId } from "./anchors";
import { UnreadableActions } from "./UnreadableActions";

/**
 * Whether the analysis leaves the document out: nothing could read it. The library names these
 * as excluded and the row offers to replace them, so both ask this one question.
 */
export function leftOut(file: FileResponse): boolean {
  return file.read_status === "not_read";
}

/** The two values the attorney chooses between, in the order the design lists them. */
export const LITERATURE_TYPES: readonly {
  value: LiteratureType;
  label: string;
}[] = [
  { value: "patent_literature", label: "Patent literature" },
  { value: "non_patent_literature", label: "Non-patent literature" },
];

const UNSET_PROMPT = "Define what this is…";
/** A document the analysis will not read needs no type; the attorney may still set one. */
const UNSET_QUIET = "Type optional";

/** Each type's pill, in the design's colours: orange for patents, yellow for the rest. */
const TYPE_TONE: Record<LiteratureType, PillTone> = {
  patent_literature: "provisional",
  non_patent_literature: "warning",
};

/**
 * The type pill: what the document is, as the design tags it, and the menu
 * that changes it. Unset, it asks in the product's own colour, and a document
 * the analysis will not read asks quietly, since it needs no type. Nothing
 * here is ever pre-set.
 */
function TypeControl({
  file,
  onChange,
}: {
  file: FileResponse;
  onChange: (value: LiteratureType) => void;
}) {
  const current = file.literature_type;
  const set = LITERATURE_TYPES.find((type) => type.value === current);
  const quiet = leftOut(file);
  const words = set?.label ?? (quiet ? UNSET_QUIET : UNSET_PROMPT);
  return (
    <Menu
      name={`${words}. What is ${file.original_filename}?`}
      triggerId={priorArtTypeId(file.file_id)}
      align="start"
      trigger={
        set === undefined
          ? { content: words, pill: quiet ? "neutral" : "brand" }
          : { content: words, pill: TYPE_TONE[set.value], caps: true }
      }
    >
      {LITERATURE_TYPES.map((type) => (
        <MenuItem
          key={type.value}
          title={type.label}
          checked={type.value === current}
          onSelect={() => onChange(type.value)}
        />
      ))}
    </Menu>
  );
}

/** The reference id and the name. */
function Identity({ file }: { file: FileResponse }) {
  return (
    <p className="truncate text-[13px] font-semibold">
      {file.reference_id !== null && (
        <span className="font-mono">{file.reference_id} · </span>
      )}
      {file.original_filename}
    </p>
  );
}

/** Where the document came from, when it was not added here. */
function OriginTag({ file }: { file: FileResponse }) {
  if (file.source !== "intake") return null;
  return (
    <StatusPill tone="brand" size="sm">
      Added during intake
    </StatusPill>
  );
}

/**
 * Removing a document from the library. Quiet until the pointer reaches it,
 * which is what a handle beside a file name should be: the row is about the
 * document rather than about deleting it.
 */
function DeleteHandle({
  file,
  onDelete,
}: {
  file: FileResponse;
  onDelete: (file: FileResponse) => void;
}) {
  return (
    <IconButton
      variant="secondary"
      size="dense"
      type="button"
      onClick={() => {
        onDelete(file);
      }}
      aria-label={`Delete ${file.original_filename}`}
      className="text-muted-foreground hover:border-destructive/40 hover:text-destructive"
    >
      <Trash2 className="h-3.5 w-3.5" />
    </IconButton>
  );
}

/**
 * Where the disclosure's account of the document differs from what the
 * document says: each point once, naming what each of the two says. The
 * disclosure can name one document twice, and the same point then comes once
 * for each name.
 */
function ArtAccount({ disagreements }: { disagreements: readonly string[] }) {
  const points = [...new Set(disagreements)];
  if (points.length === 0) return null;
  return (
    <Panel
      shape="row"
      pad="tight"
      tone="warning"
      data-testid="art-account"
      className="text-[11.5px]"
    >
      <MicroLabel as="p" size="sm" className="mb-0.5">
        The disclosure's account differs
      </MicroLabel>
      <ul className="grid gap-0.5">
        {points.map((said) => (
          <li key={said}>{said}</li>
        ))}
      </ul>
    </Panel>
  );
}

/**
 * One prior art document: its id and name, what the attorney says it is,
 * where it came from, whether it could be read, a way to open it, and where
 * the disclosure's account of it differs from its text.
 */
export function PriorArtRow({
  file,
  taskId,
  accept,
  selected,
  disagreements,
  onSelect,
  onClassify,
  onView,
  onDelete,
  onReplace,
}: {
  file: FileResponse;
  taskId: string;
  accept: Record<string, string[]>;
  selected: boolean;
  disagreements: readonly string[];
  onSelect: (fileId: string, selected: boolean) => void;
  onClassify: (fileId: string, value: LiteratureType | null) => void;
  onView: (file: FileResponse) => void;
  onDelete: (file: FileResponse) => void;
  onReplace: (file: FileResponse, replacement: File) => Promise<void>;
}) {
  const unreadable = leftOut(file);
  const flagged = needsAttention(file.read_status);
  return (
    <Panel
      as="li"
      shape="row"
      data-testid={`prior-art-row-${file.file_id}`}
      data-selected={selected ? "true" : "false"}
      data-attention={flagged}
      edge={flagged ? "warning" : undefined}
      className={cn(
        "mb-2 grid grid-cols-[auto_auto_minmax(0,1fr)_auto] items-center gap-x-3",
        selected ? "border-primary/40 bg-primary/[0.03]" : "border-border",
      )}
    >
      <Checkbox
        aria-label={`Select ${file.original_filename}`}
        checked={selected}
        onCheckedChange={(checked) => onSelect(file.file_id, checked === true)}
      />
      <FileMark contentType={file.content_type} />
      <div className="flex min-w-0 flex-col gap-1.5">
        <Identity file={file} />
        <div className="flex flex-wrap items-center gap-2">
          <TypeControl
            file={file}
            onChange={(value) => onClassify(file.file_id, value)}
          />
          {unreadable && (
            <UnreadableActions
              file={file}
              taskId={taskId}
              accept={accept}
              onReplace={onReplace}
            />
          )}
          <ArtAccount disagreements={disagreements} />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <OriginTag file={file} />
        <ReadStatusChip read={readOf(file)} />
        <span className="text-[11.5px] text-muted-foreground">
          <OpenDocument
            onOpen={() => onView(file)}
            label={`View ${file.original_filename}`}
          >
            View
          </OpenDocument>
        </span>
        <DeleteHandle file={file} onDelete={onDelete} />
      </div>
    </Panel>
  );
}
